/** Display helpers shared across the Allocation Manager surfaces. */

export const NULL_DISPLAY_VALUE = '--';

// Stored enum values map to display labels explicitly — `humanize`-style
// derivation would render 'd_and_s' as "D and s".
const UNIT_AGREEMENT_TYPE_LABELS: Record<string, string> = {
  d_and_s: 'D&S',
  joa: 'JOA',
};

/**
 * Renders a `unit_agreement_type` enum value for display.
 *
 * @param {string | null | undefined} type - Stored enum value, or null when untyped.
 * @return {string} 'D&S', 'JOA', or the null placeholder for unset/unrecognised values.
 */
export const formatUnitAgreementType = (
  type: string | null | undefined
): string => UNIT_AGREEMENT_TYPE_LABELS[type ?? ''] ?? NULL_DISPLAY_VALUE;

// 'Best_fit' is stored capitalised with an underscore; an unmapped value falls
// back to the raw string so an unexpected type is still legible in the table.
const ASSIGNMENT_TYPE_LABELS: Record<string, string> = {
  Explicit: 'Explicit',
  Best_fit: 'Best Fit',
};

/**
 * Renders an `assignment_type` value for display.
 *
 * @param {string | null | undefined} type - Stored value, e.g. 'Explicit' or 'Best_fit'.
 * @return {string} The display label, the raw value when unmapped, or the null placeholder.
 */
export const formatAssignmentType = (type: string | null | undefined): string =>
  ASSIGNMENT_TYPE_LABELS[type ?? ''] ?? (type || NULL_DISPLAY_VALUE);

const ALLOCATION_METHOD_LABELS: Record<string, string> = {
  acreage: 'Acreage',
  lateral_length: 'Lateral Length',
};

/**
 * Renders an `allocation_method` enum value for display.
 *
 * @param {string | null | undefined} method - Stored enum value.
 * @return {string} 'Acreage', 'Lateral Length', or the null placeholder.
 */
export const formatAllocationMethod = (
  method: string | null | undefined
): string => ALLOCATION_METHOD_LABELS[method ?? ''] ?? NULL_DISPLAY_VALUE;
